import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectActiveFirmIdN } from "redux/slices/client";
import { fetchPendingReviewsByAssignee, selectPendingReviewsByAssignee } from "redux/slices/clientLens";
import theme from "theme";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { maxBy } from "helpers/coll";
import formatHighchartsNumber from "helpers/formatHighchartsNumber";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { GetPendingCasesStatsResult } from "generated/graphql";
import { fullName } from "helpers/name-helpers";
import { useNavigate } from "react-router";
import { AppRoutes } from "AppRoutes";

interface PendingReviewPoint extends Highcharts.Point {
  custom: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  };
}

interface PendingReviewsProps {
  lensId: number;
  height: number;
}

const PendingReviews = (props: PendingReviewsProps) => {
  const { lensId, height } = props;
  const snackpatch = useAppDispatchWithSnackbar();
  const pendingReviewsByAssignee = useAppSelector(selectPendingReviewsByAssignee);
  const activeFirmId = useAppSelector(selectActiveFirmIdN);
  const [options, setOptions] = useState<Highcharts.Options>({
    chart: {
      type: "bar",
      height,
    },
    title: {
      text: "",
    },
    xAxis: {
      type: "category",
      labels: {
        style: {
          fontFamily: "Inter,arial,sans-serif",
          fontSize: "10px",
        },
      },
      min: 0,
      max: 7,
      tickLength: 0,
    },
    yAxis: {
      min: 0,
      visible: false,
    },
    tooltip: {
      formatter() {
        const point = this.point as PendingReviewPoint;
        return `${point.custom?.firstName} ${point.custom?.lastName}<br>
                ${point.custom?.email} - <b>${formatHighchartsNumber(this.y ? this.y : 0)}</b>`;
      },
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          formatter() {
            return this.y ? formatHighchartsNumber(this.y) : 0;
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: [],
  });

  useEffect(() => {
    const maxYAxis = maxBy(
      pendingReviewsByAssignee.length ? pendingReviewsByAssignee.map((p) => p.count) : [0, 0],
      (t1, t2) => t1 - t2
    );

    setOptions({
      xAxis: {
        max: pendingReviewsByAssignee.length < 8 ? pendingReviewsByAssignee.length - 1 : 7,
        uniqueNames: false,
        scrollbar: {
          enabled: pendingReviewsByAssignee.length > 8,
        },
      },
      yAxis: {
        max: !maxYAxis ? 1 : null,
      },
      series: pendingReviewsByAssignee.reduce(
        (prev, stat) => {
          prev[0]?.data.push({
            name: stat.displayName,
            y: stat.count,
            custom: {
              email: stat.email,
              firstName: stat.firstName,
              lastName: stat.lastName,
            },
          });

          return prev;
        },
        [
          {
            name: "Pending Reviews",
            type: "bar",
            color: theme.palette.primary.main,
            showInLegend: false,
            data: [] as any[],
          },
        ]
      ),
    });
  }, [pendingReviewsByAssignee, lensId]);

  useEffect(() => {
    snackpatch(fetchPendingReviewsByAssignee({ lensId, firmId: activeFirmId }), {
      errorMessage: "Failed to fetch pending reviews",
    });
  }, [lensId, activeFirmId]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default PendingReviews;

export function SBRPendingReviews(props: Omit<PendingReviewsProps, "height"> & { stats: GetPendingCasesStatsResult }) {
  const { lensId, stats } = props;
  const navigate = useNavigate();

  const options: Highcharts.Options = useMemo(() => {
    const assigneeCounts = stats.byAssignee.map((p) => p.count);
    const maxYAxis = maxBy([...assigneeCounts, stats.unassignedCount], (t1, t2) => t1 - t2);

    return {
      title: {
        text: "",
      },
      chart: {
        type: "bar",
      },
      xAxis: {
        type: "category",
        labels: {
          style: {
            fontFamily: "Inter,arial,sans-serif",
            fontSize: "12px",
            fontWeight: "600",
          },
        },
        min: 0,
        tickLength: 0,
        max: stats.byAssignee.length < 8 ? stats.byAssignee.length : 7,
        uniqueNames: false,
        scrollbar: {
          enabled: stats.byAssignee.length > 8,
        },
      },
      yAxis: {
        max: !maxYAxis ? 1 : null,
        min: 0,
        visible: false,
      },
      tooltip: {
        formatter() {
          const point = this.point as PendingReviewPoint;
          return `${point.custom?.firstName} ${point.custom?.lastName}<br>
                      ${formatHighchartsNumber(this.y ? this.y : 0)}`;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: "4px",
          allowPointSelect: false,
          dataLabels: {
            enabled: true,
            formatter() {
              return this.y ? formatHighchartsNumber(this.y) : 0;
            },
          },
        },
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          name: "Pending Reviews",
          type: "bar",
          color: theme.palette.primary.main,
          showInLegend: false,
          cursor: "pointer",
          point: {
            events: {
              click: function (e: Highcharts.PointClickEventObject) {
                e.stopPropagation();
                const point = this as PendingReviewPoint;
                navigate(AppRoutes.caseTable({ lensId, sectionAssignees: [point.custom.id] }));
              },
            },
          },
          data: [
            {
              name: "Unassigned",
              y: stats.unassignedCount,
              custom: {
                id: -1,
                email: "",
                firstName: "",
                lastName: "",
              },
            },
            ...stats.byAssignee.map((stat) => ({
              name: fullName(stat.assignee),
              y: stat.count,
              custom: {
                id: stat.assignee.id,
                email: stat.assignee.email,
                firstName: stat.assignee.firstName,
                lastName: stat.assignee.lastName,
              },
            })),
          ],
        },
      ],
    };
  }, [stats, lensId]);

  return <HighchartsReact highcharts={Highcharts} options={options} containerProps={{ className: "h-full" }} />;
}
